import { useEffect, useMemo, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Query, UseQueryResult, useQueries, useQueryClient } from '@tanstack/react-query';
import {
  pollingTaskAtom,
  prepareTaskAtom,
  tasksStatusAtom,
  updateTaskResultAtom,
  updateTaskStatusAtom,
} from '../@atoms';
import { Task, TaskId } from '../@types';
import { getTaskParamFromTaskDescription } from '../@utils';

// todo-jamie: PrepareRes, PollingRes, CancelRes, FailedRes 등의 제네릭 타입을 어떻게 연결하지..
const PREPARE_QUEUE_KEY = 'download-monitoring-panel-prepare-queue';
export const usePrepareTaskRunner = () => {
  const prepareTaskQueue = useAtomValue(prepareTaskAtom);
  const updateTaskStatus = useSetAtom(updateTaskStatusAtom);

  const queries = useMemo(() => {
    return prepareTaskQueue.map((task) => ({
      queryKey: [PREPARE_QUEUE_KEY, task.taskId],
      queryFn: async ({ signal }: { signal?: AbortSignal }) => {
        try {
          const res = (await task.prepareFn?.(signal)) as any;
          return { ...res, taskId: task.taskId };
        } catch (error) {
          if (signal?.aborted) {
            const currentTaskParam = getTaskParamFromTaskDescription(task, { status: 'removed', result: undefined });
            updateTaskStatus(currentTaskParam);
            await task.removeFn(currentTaskParam);
          } else {
            throw error;
          }
        }
      },
      onError: (error: any) => {
        const targetTask = prepareTaskQueue.find((t) => t.taskId === task.taskId);
        if (targetTask) {
          targetTask.onFailed?.(targetTask, error);
          updateTaskStatus({
            taskId: targetTask.taskId,
            processId: null,
            status: 'failed',
          });

          targetTask.onFailed?.(
            {
              taskId: targetTask.taskId,
              fileName: targetTask.fileName,
              fileType: targetTask.fileType,
              status: 'failed',
              processId: null,
            },
            error
          );
        }
      },
    }));
  }, [JSON.stringify(prepareTaskQueue)]);

  const readyTaskRes: UseQueryResult<{ taskId: TaskId; processId: number }>[] = useQueries({
    queries,
  });

  const currentReadyTaskRes = useMemo(() => {
    return readyTaskRes.map((res) => {
      return {
        isSuccess: res.isSuccess,
        data: res.data,
        isError: res.isError,
        failureReason: res.failureReason,
      };
    });
  }, [JSON.stringify(readyTaskRes)]);

  useEffect(() => {
    currentReadyTaskRes.forEach((prepareRes) => {
      if (prepareRes.data && prepareRes.isSuccess) {
        const prepareResTaskId = prepareRes.data.taskId;
        const prepareResProcessId = prepareRes.data.processId;

        const targetTask = prepareTaskQueue.find((task) => task.taskId === prepareResTaskId);

        if (targetTask) {
          updateTaskStatus({
            taskId: prepareResTaskId,
            processId: prepareResProcessId,
            status: 'processing',
          });

          targetTask.onPrepared?.(
            {
              taskId: targetTask.taskId,
              processId: prepareResProcessId,
              fileName: targetTask.fileName,
              fileType: targetTask.fileType,
              status: targetTask.status,
            },
            prepareRes.data
          );
        }
      }
    });
  }, [JSON.stringify(currentReadyTaskRes)]);
};

const POLLING_QUEUE_KEY = 'download-monitoring-panel-polling-queue';
export const usePollingTaskRunner = () => {
  const pollingApiQueue = useAtomValue(pollingTaskAtom);
  const updateTaskStatus = useSetAtom(updateTaskStatusAtom);
  const updateTaskResult = useSetAtom(updateTaskResultAtom);

  const stableIdsInProgressState = useRef({ inProgress: [] as TaskId[], completed: [] as TaskId[], remainCount: 0 });

  const queries = useMemo(
    () =>
      pollingApiQueue.map((task) => ({
        queryKey: [POLLING_QUEUE_KEY, task.taskId],
        queryFn: async ({ signal }: { signal?: AbortSignal }) => {
          try {
            const res = await task.pollingFn(getTaskParamFromTaskDescription(task, { status: 'processing' }), signal);
            return res;
          } catch (error) {
            if (signal?.aborted) {
              const currentTaskParam = getTaskParamFromTaskDescription(task, { status: 'removed', result: undefined });
              updateTaskStatus(currentTaskParam);
              await task.removeFn(currentTaskParam);
            } else {
              throw error;
            }
          }
        },
        refetchInterval: (response: any, query: Query) => {
          // note-@jamie: queryFn 이 실행되기 전에 refetchInterval가 먼저 한번 실행된다

          if (query.state.error) {
            updateTaskStatus({
              taskId: task.taskId,
              processId: task.processId,
              status: 'failed',
            });
            return false;
          } else if (response) {
            const [_, taskId] = query.queryKey as [string, TaskId];

            if (response.completed) {
              if (stableIdsInProgressState.current.completed.includes(taskId)) {
                // 혹시모를 재호출 방지
                return false;
              }
              stableIdsInProgressState.current = {
                inProgress: stableIdsInProgressState.current.inProgress.filter((id) => id !== taskId),
                completed: [...stableIdsInProgressState.current.completed, taskId],
                remainCount: stableIdsInProgressState.current.remainCount - 1,
              };

              task.onCompleted?.(
                {
                  taskId,
                  processId: task.processId,
                  fileName: task.fileName,
                  fileType: task.fileType,
                  status: 'completed',
                  result: response,
                },
                response,
                (result) => {
                  updateTaskResult({
                    taskId,
                    result,
                  });
                }
              );

              updateTaskStatus({
                taskId,
                processId: task.processId,
                status: 'completed',
                progress: 100,
              });
              updateTaskResult({
                taskId,
                result: response,
              });

              return false;
            } else if (typeof response?.progress === 'number') {
              task.onProgress?.(
                {
                  taskId,
                  processId: task.processId,
                  fileName: task.fileName,
                  fileType: task.fileType,
                  status: 'processing',
                },
                response
              );
              updateTaskStatus({
                taskId,
                processId: task.processId,
                status: 'processing',
                progress: response.progress,
              });
            }
            return task.pollingInterval;
          }
          return task.pollingInterval;
        },
        onError: (error: any) => {
          // callback 함수에 전달하기 위한 용도
          const currentTaskParam: Task = {
            taskId: task.taskId,
            processId: task.processId,
            fileName: task.fileName,
            fileType: task.fileType,
            status: 'failed',
          };
          task.onFailed?.(currentTaskParam, error);
        },
        staleTime: Infinity, // 데이터가 'stale'해지지 않도록 설정하여 불필요한 즉시 refetch 방지
      })),
    [JSON.stringify(pollingApiQueue)]
  );

  useQueries({
    queries,
  });

  const tasksStatus = useAtomValue(tasksStatusAtom);
  const queryClient = useQueryClient();
  useEffect(() => {
    tasksStatus.forEach((task) => {
      if (task.status === 'removed') {
        queryClient.cancelQueries({ queryKey: [POLLING_QUEUE_KEY, task.taskId] });
      }
    });

    const inProgress = tasksStatus
      .filter((task) => task.status === 'processing' || task.status === 'prepare')
      .map((task) => task.taskId);
    const completed = tasksStatus.filter((task) => task.status === 'completed').map((task) => task.taskId);
    stableIdsInProgressState.current = {
      inProgress,
      completed,
      remainCount: inProgress.length,
    };
  }, [JSON.stringify(tasksStatus)]);
};
